/**
 * Bounds a promise that has no timeout of its own. Rejects with a TimeoutError-named
 * Error after `ms` so callers can tell a ceiling from a real failure.
 *
 * The underlying operation is NOT cancelled (most of the call sites wrap third-party
 * promises with no abort support); it is left to settle unobserved. Use this to stop
 * one hung dependency from freezing everything awaiting it, not as a cancellation
 * mechanism.
 */
export async function withTimeout<T>(
    promise: Promise<T>,
    ms: number,
    description: string
): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
        return await Promise.race([
            promise,
            new Promise<never>((_, reject) => {
                timeoutId = setTimeout(() => {
                    const error = new Error(
                        `${description} timed out after ${Math.round(ms / 1000)}s.`
                    );
                    error.name = 'TimeoutError';
                    reject(error);
                }, ms);
            })
        ]);
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        // Late failures of the abandoned promise must not surface as unhandled rejections.
        promise.catch(() => undefined);
    }
}

export default withTimeout;
